import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useCareerActivation } from "./useCareerActivation";
import { eligibleCareerEvents } from "@/data/careerEventBank";

/* ─── Event Template Types ─── */

export interface EventOption {
  label: string;
  accountType: string;
  amount: number;
  isDeduction: boolean;
  explanation: string;
}

export interface EventTemplate {
  key: string;
  type: "money_event" | "life_event" | "economic_event" | "entrepreneurship_event";
  title: string;
  description: string;
  amount: number;
  difficulty: "early" | "mid" | "advanced";
  options: EventOption[];
}

export interface ActiveEvent {
  template: EventTemplate;
  lessonId: string;
}

/* ─── Money Event Pool ─── */

const MONEY_EVENTS: EventTemplate[] = [
  {
    key: "birthday_gift", type: "money_event", title: "Birthday Gift 🎉",
    description: "A relative sent you $150 for your birthday! How will you use it?",
    amount: 150, difficulty: "early",
    options: [
      { label: "Keep it in Checking", accountType: "checking", amount: 150, isDeduction: false, explanation: "Easy access, but it might get spent quickly. Keeping cash liquid is fine short-term." },
      { label: "Move to Savings", accountType: "savings", amount: 150, isDeduction: false, explanation: "Smart choice! Savings grow over time and help you reach goals faster." },
      { label: "Build Emergency Fund", accountType: "emergency_fund", amount: 150, isDeduction: false, explanation: "Excellent discipline! Emergency funds protect you from unexpected expenses." },
    ],
  },
  {
    key: "cashback_reward", type: "money_event", title: "Cashback Reward 💳",
    description: "Your responsible credit use earned you a $75 cashback reward!",
    amount: 75, difficulty: "early",
    options: [
      { label: "Add to Checking", accountType: "checking", amount: 75, isDeduction: false, explanation: "Available for immediate use. Just be intentional about spending it." },
      { label: "Save it", accountType: "savings", amount: 75, isDeduction: false, explanation: "Treating rewards as savings accelerates your financial growth." },
    ],
  },
  {
    key: "side_hustle_pay", type: "money_event", title: "Side Hustle Payout 💼",
    description: "Your weekend freelance work earned you $300. What's the move?",
    amount: 300, difficulty: "mid",
    options: [
      { label: "Checking for spending", accountType: "checking", amount: 300, isDeduction: false, explanation: "Income is great, but spending it all misses the chance to build wealth." },
      { label: "Split: half Savings, half Checking", accountType: "savings", amount: 150, isDeduction: false, explanation: "The 50/50 split balances enjoying your earnings with building for the future." },
      { label: "Invest it all", accountType: "investment", amount: 300, isDeduction: false, explanation: "Aggressive wealth building! Investing extra income is how real wealth compounds." },
      { label: "Emergency Fund boost", accountType: "emergency_fund", amount: 300, isDeduction: false, explanation: "Security first! A strong emergency fund means financial peace of mind." },
    ],
  },
  {
    key: "tax_refund", type: "money_event", title: "Tax Refund 📊",
    description: "You received a $500 tax refund. This is money you already earned. Use it wisely.",
    amount: 500, difficulty: "mid",
    options: [
      { label: "Keep in Checking", accountType: "checking", amount: 500, isDeduction: false, explanation: "Convenient but easy to spend. A tax refund is a great opportunity to get ahead." },
      { label: "Boost Savings", accountType: "savings", amount: 500, isDeduction: false, explanation: "Putting windfalls into savings is one of the best financial habits you can build." },
      { label: "Invest for growth", accountType: "investment", amount: 500, isDeduction: false, explanation: "Investing a lump sum lets compound growth work in your favor over time." },
      { label: "Start retirement fund", accountType: "roth_ira", amount: 500, isDeduction: false, explanation: "Contributing early to retirement is the single most powerful wealth-building decision." },
    ],
  },
  {
    key: "scholarship_bonus", type: "money_event", title: "Scholarship Award 🎓",
    description: "Your academic performance earned a $250 scholarship bonus!",
    amount: 250, difficulty: "early",
    options: [
      { label: "Add to Checking", accountType: "checking", amount: 250, isDeduction: false, explanation: "Scholarship money in checking may get spent on non-essentials." },
      { label: "Save for a goal", accountType: "savings", amount: 250, isDeduction: false, explanation: "Earmarking scholarship money for goals shows strong financial planning." },
    ],
  },
  {
    key: "investment_gain", type: "money_event", title: "Investment Gain 📈",
    description: "Your portfolio generated a $200 return! How do you handle the gain?",
    amount: 200, difficulty: "advanced",
    options: [
      { label: "Cash out to Checking", accountType: "checking", amount: 200, isDeduction: false, explanation: "Taking gains is fine, but reinvesting allows compound growth." },
      { label: "Reinvest", accountType: "investment", amount: 200, isDeduction: false, explanation: "Reinvesting gains is how portfolios grow exponentially over time." },
      { label: "Diversify to Roth IRA", accountType: "roth_ira", amount: 200, isDeduction: false, explanation: "Moving gains to retirement accounts provides tax-advantaged growth." },
    ],
  },
  {
    key: "business_profit", type: "money_event", title: "Business Profit 🏪",
    description: "Your business simulation generated $400 in profit this quarter!",
    amount: 400, difficulty: "advanced",
    options: [
      { label: "Take the profit", accountType: "checking", amount: 400, isDeduction: false, explanation: "Paying yourself is important, but reinvesting grows the business faster." },
      { label: "Reinvest in business", accountType: "investment", amount: 400, isDeduction: false, explanation: "Reinvesting profits is how businesses scale and create long-term value." },
      { label: "Build a reserve", accountType: "emergency_fund", amount: 400, isDeduction: false, explanation: "Business reserves protect against slow periods and unexpected costs." },
      { label: "Save for expansion", accountType: "savings", amount: 400, isDeduction: false, explanation: "Saving profits strategically prepares you for future growth opportunities." },
    ],
  },
  {
    key: "found_money", type: "money_event", title: "Lucky Find 🍀",
    description: "You discovered $50 in an old jacket pocket. Small wins matter!",
    amount: 50, difficulty: "early",
    options: [
      { label: "Pocket it (Checking)", accountType: "checking", amount: 50, isDeduction: false, explanation: "Small amounts add up. Even $50 saved consistently builds wealth." },
      { label: "Save it", accountType: "savings", amount: 50, isDeduction: false, explanation: "Saving even small windfalls creates a powerful savings habit." },
    ],
  },
  {
    key: "unexpected_bonus", type: "money_event", title: "Unexpected Bonus 🎁",
    description: "Your employer gave you a surprise $350 performance bonus! What will you do?",
    amount: 350, difficulty: "mid",
    options: [
      { label: "Spend it (Checking)", accountType: "checking", amount: 350, isDeduction: false, explanation: "Treating yourself is okay, but windfalls are great opportunities to get ahead." },
      { label: "Save for the future", accountType: "savings", amount: 350, isDeduction: false, explanation: "Saving bonuses instead of spending them is a wealth-building superpower." },
      { label: "Invest for growth", accountType: "investment", amount: 350, isDeduction: false, explanation: "Investing a bonus lets compound interest do the heavy lifting." },
    ],
  },
  {
    key: "freelance_payment", type: "money_event", title: "Freelance Payment 💻",
    description: "A client paid you $225 for a freelance project you completed!",
    amount: 225, difficulty: "mid",
    options: [
      { label: "Add to Checking", accountType: "checking", amount: 225, isDeduction: false, explanation: "Freelance income in checking gives flexibility but may get spent fast." },
      { label: "Build Emergency Fund", accountType: "emergency_fund", amount: 225, isDeduction: false, explanation: "Freelancers especially need emergency funds for irregular income months." },
      { label: "Invest in yourself", accountType: "savings", amount: 225, isDeduction: false, explanation: "Saving freelance income creates a buffer and funds future opportunities." },
    ],
  },
  {
    key: "rental_income", type: "money_event", title: "Rental Income 🏠",
    description: "You received $600 in rental income from your investment property!",
    amount: 600, difficulty: "advanced",
    options: [
      { label: "Take as income", accountType: "checking", amount: 600, isDeduction: false, explanation: "Rental income can supplement your cash flow, but reinvesting builds more wealth." },
      { label: "Reinvest in property", accountType: "investment", amount: 600, isDeduction: false, explanation: "Reinvesting rental income into properties compounds your real estate portfolio." },
      { label: "Save for maintenance", accountType: "emergency_fund", amount: 600, isDeduction: false, explanation: "Setting aside rental income for repairs prevents future financial stress." },
    ],
  },
  {
    key: "sold_old_item", type: "money_event", title: "Sold an Old Item 📦",
    description: "You sold unused electronics online for $120. Decluttering pays!",
    amount: 120, difficulty: "early",
    options: [
      { label: "Keep in Checking", accountType: "checking", amount: 120, isDeduction: false, explanation: "Quick cash, but saving it would be even smarter." },
      { label: "Add to Savings", accountType: "savings", amount: 120, isDeduction: false, explanation: "Turning clutter into savings is a great financial habit!" },
    ],
  },
  {
    key: "marketplace_profit", type: "money_event", title: "Marketplace Sale Profit 🛒",
    description: "You flipped a vintage item for $275 profit on the marketplace!",
    amount: 275, difficulty: "mid",
    options: [
      { label: "Cash out (Checking)", accountType: "checking", amount: 275, isDeduction: false, explanation: "Enjoy the profit, but consider reinvesting for more gains." },
      { label: "Save the profit", accountType: "savings", amount: 275, isDeduction: false, explanation: "Saving side income profits is how small hustles become big wins." },
      { label: "Invest the gains", accountType: "investment", amount: 275, isDeduction: false, explanation: "Investing profits from side hustles accelerates wealth building." },
    ],
  },
  {
    key: "gift_money", type: "money_event", title: "Gift Money 💝",
    description: "A family member gave you $200 as a holiday gift!",
    amount: 200, difficulty: "early",
    options: [
      { label: "Pocket it (Checking)", accountType: "checking", amount: 200, isDeduction: false, explanation: "Gift money in checking is easy to spend. Consider saving some!" },
      { label: "Save half, spend half", accountType: "savings", amount: 100, isDeduction: false, explanation: "The 50/50 rule with gift money teaches balanced financial habits." },
      { label: "All to Savings", accountType: "savings", amount: 200, isDeduction: false, explanation: "Saving gift money shows maturity and accelerates your financial goals." },
    ],
  },
  {
    key: "dividend_income", type: "money_event", title: "Investment Dividend 💰",
    description: "Your stock portfolio paid out $85 in quarterly dividends!",
    amount: 85, difficulty: "advanced",
    options: [
      { label: "Reinvest dividends", accountType: "investment", amount: 85, isDeduction: false, explanation: "Dividend reinvestment is one of the most powerful compounding strategies." },
      { label: "Take as cash", accountType: "checking", amount: 85, isDeduction: false, explanation: "Taking dividends as cash reduces compounding power but provides liquidity." },
      { label: "Add to retirement", accountType: "roth_ira", amount: 85, isDeduction: false, explanation: "Routing dividends to retirement accounts maximizes long-term tax-free growth." },
    ],
  },
];

/* ─── Life Event Pool ─── */

const LIFE_EVENTS: EventTemplate[] = [
  {
    key: "broken_phone", type: "life_event", title: "Broken Phone 📱",
    description: "Your phone screen cracked and needs a $180 repair. How do you handle it?",
    amount: 180, difficulty: "early",
    options: [
      { label: "Pay from Checking", accountType: "checking", amount: 180, isDeduction: true, explanation: "Using checking for small emergencies works if your balance is healthy." },
      { label: "Use Emergency Fund", accountType: "emergency_fund", amount: 180, isDeduction: true, explanation: "This is exactly what emergency funds are for — unexpected necessary expenses." },
      { label: "Put on Credit", accountType: "checking", amount: 0, isDeduction: false, explanation: "Using credit for emergencies can work, but you'll owe interest if you don't pay it off quickly." },
    ],
  },
  {
    key: "car_repair", type: "life_event", title: "Car Trouble 🔧",
    description: "Your vehicle needs a $350 brake repair. It's not safe to drive without it.",
    amount: 350, difficulty: "mid",
    options: [
      { label: "Pay from Checking", accountType: "checking", amount: 350, isDeduction: true, explanation: "If your checking can absorb this, it's the simplest solution." },
      { label: "Use Emergency Fund", accountType: "emergency_fund", amount: 350, isDeduction: true, explanation: "Emergency funds exist for exactly this kind of surprise. Smart use!" },
      { label: "Dip into Savings", accountType: "savings", amount: 350, isDeduction: true, explanation: "Using savings for repairs delays your goals. Try to rebuild savings afterward." },
    ],
  },
  {
    key: "medical_expense", type: "life_event", title: "Medical Bill 🏥",
    description: "An unexpected doctor visit costs $275. Health always comes first.",
    amount: 275, difficulty: "mid",
    options: [
      { label: "Pay from Checking", accountType: "checking", amount: 275, isDeduction: true, explanation: "Medical expenses are unavoidable. Having liquidity for health costs is essential." },
      { label: "Use Emergency Fund", accountType: "emergency_fund", amount: 275, isDeduction: true, explanation: "Medical bills are a textbook emergency fund use case. Well done." },
      { label: "Split across accounts", accountType: "checking", amount: 140, isDeduction: true, explanation: "Splitting the impact across accounts preserves balance in each." },
    ],
  },
  {
    key: "pet_emergency", type: "life_event", title: "Pet Emergency 🐾",
    description: "Your pet needs urgent vet care costing $400. They're counting on you.",
    amount: 400, difficulty: "mid",
    options: [
      { label: "Pay from Checking", accountType: "checking", amount: 400, isDeduction: true, explanation: "Paying upfront avoids debt, but may strain your cash flow." },
      { label: "Use Emergency Fund", accountType: "emergency_fund", amount: 400, isDeduction: true, explanation: "Pet emergencies are real emergencies. Your fund is there for moments like this." },
      { label: "Sell some investments", accountType: "investment", amount: 400, isDeduction: true, explanation: "Selling investments for emergencies interrupts compound growth. Build your emergency fund to avoid this." },
    ],
  },
  {
    key: "school_expense", type: "life_event", title: "School Supplies 📚",
    description: "Unexpected school fees of $120 are due this week.",
    amount: 120, difficulty: "early",
    options: [
      { label: "Pay from Checking", accountType: "checking", amount: 120, isDeduction: true, explanation: "Small expected costs are best handled from your checking flow." },
      { label: "Use Savings", accountType: "savings", amount: 120, isDeduction: true, explanation: "Using savings for routine costs can erode your long-term goals." },
    ],
  },
  {
    key: "rent_increase", type: "life_event", title: "Rent Increase 🏠",
    description: "Your monthly expenses just went up by $200 due to a rent increase. Cover this month's difference.",
    amount: 200, difficulty: "advanced",
    options: [
      { label: "Absorb from Checking", accountType: "checking", amount: 200, isDeduction: true, explanation: "Adjusting your budget for higher costs is part of adult financial planning." },
      { label: "Use Emergency Fund temporarily", accountType: "emergency_fund", amount: 200, isDeduction: true, explanation: "Using emergency funds for ongoing costs is risky. Plan to adjust your budget instead." },
      { label: "Reduce investments", accountType: "investment", amount: 200, isDeduction: true, explanation: "Pulling from investments for recurring costs hurts long-term growth. Budget adjustment is better." },
    ],
  },
  {
    key: "stolen_item", type: "life_event", title: "Stolen Belongings 🚨",
    description: "Someone stole your backpack with $150 worth of items. You need replacements.",
    amount: 150, difficulty: "early",
    options: [
      { label: "Replace from Checking", accountType: "checking", amount: 150, isDeduction: true, explanation: "Replacing essentials from checking is the most straightforward approach." },
      { label: "Use Emergency Fund", accountType: "emergency_fund", amount: 150, isDeduction: true, explanation: "Theft qualifies as an emergency. This is a valid use of your fund." },
    ],
  },
  {
    key: "appliance_failure", type: "life_event", title: "Appliance Breakdown ⚡",
    description: "Your refrigerator broke down. Repair costs $500. Food is spoiling.",
    amount: 500, difficulty: "advanced",
    options: [
      { label: "Pay from Checking", accountType: "checking", amount: 500, isDeduction: true, explanation: "A large hit to checking. This is why building reserves matters." },
      { label: "Emergency Fund", accountType: "emergency_fund", amount: 500, isDeduction: true, explanation: "This is exactly the kind of large unexpected cost emergency funds handle." },
      { label: "Split Checking + Savings", accountType: "checking", amount: 250, isDeduction: true, explanation: "Splitting reduces the shock to any single account." },
      { label: "Put on Credit", accountType: "checking", amount: 0, isDeduction: false, explanation: "Credit can bridge the gap, but you'll need to pay it back with interest." },
    ],
  },
  {
    key: "market_downturn", type: "life_event", title: "Market Downturn 📉",
    description: "The market dropped and your investments lost $300 in value. What do you do?",
    amount: 300, difficulty: "advanced",
    options: [
      { label: "Hold steady — don't sell", accountType: "checking", amount: 0, isDeduction: false, explanation: "Staying invested during downturns is historically the best long-term strategy. Well done!" },
      { label: "Panic sell investments", accountType: "investment", amount: 300, isDeduction: true, explanation: "Selling during a dip locks in losses. Markets historically recover — patience pays." },
      { label: "Buy the dip — invest more", accountType: "investment", amount: 100, isDeduction: false, explanation: "Buying during downturns can be rewarding, but only with money you won't need soon." },
    ],
  },
  /* ─── New Life Events: Opportunities ─── */
  {
    key: "job_promotion", type: "life_event", title: "Job Promotion 🎯",
    description: "You got promoted! Your new role comes with a $500 signing bonus. How do you allocate it?",
    amount: 500, difficulty: "mid",
    options: [
      { label: "Celebrate (Checking)", accountType: "checking", amount: 500, isDeduction: false, explanation: "Celebrating milestones is important, but saving some of a raise builds lasting wealth." },
      { label: "Invest for the future", accountType: "investment", amount: 500, isDeduction: false, explanation: "Investing a promotion bonus is a classic wealth-building move." },
      { label: "Boost retirement", accountType: "roth_ira", amount: 500, isDeduction: false, explanation: "Increasing retirement contributions with each raise is the best long-term strategy." },
    ],
  },
  {
    key: "emergency_travel", type: "life_event", title: "Emergency Travel ✈️",
    description: "A family emergency requires a $450 last-minute flight. You need to go.",
    amount: 450, difficulty: "mid",
    options: [
      { label: "Pay from Checking", accountType: "checking", amount: 450, isDeduction: true, explanation: "Family emergencies are unavoidable. Having liquid cash ready is crucial." },
      { label: "Use Emergency Fund", accountType: "emergency_fund", amount: 450, isDeduction: true, explanation: "Emergency travel is a legitimate emergency fund use. This is why you save." },
      { label: "Put on Credit", accountType: "checking", amount: 0, isDeduction: false, explanation: "Credit for emergencies works short-term, but pay it off quickly to avoid interest." },
    ],
  },
  {
    key: "friends_wedding", type: "life_event", title: "Friend's Wedding Trip 💒",
    description: "Your best friend's destination wedding costs $380 for travel and gifts. Will you go?",
    amount: 380, difficulty: "mid",
    options: [
      { label: "Go all-in (Checking)", accountType: "checking", amount: 380, isDeduction: true, explanation: "Friendships matter, but make sure this fits your budget without sacrificing essentials." },
      { label: "Use Savings", accountType: "savings", amount: 380, isDeduction: true, explanation: "Using savings for lifestyle events delays financial goals. Budget for fun separately." },
      { label: "Set a smaller budget", accountType: "checking", amount: 200, isDeduction: true, explanation: "Setting boundaries on social spending is a powerful financial skill. Your friend will understand." },
    ],
  },
  {
    key: "moving_opportunity", type: "life_event", title: "Moving Opportunity 🏡",
    description: "A new apartment closer to work would save commuting costs, but moving costs $600 upfront.",
    amount: 600, difficulty: "advanced",
    options: [
      { label: "Pay from Checking", accountType: "checking", amount: 600, isDeduction: true, explanation: "If the move saves money long-term, the upfront cost is worth it." },
      { label: "Use Savings", accountType: "savings", amount: 600, isDeduction: true, explanation: "Using savings for strategic moves can pay off, but rebuild afterward." },
      { label: "Stay put — save the money", accountType: "checking", amount: 0, isDeduction: false, explanation: "Sometimes the best financial move is to stay put and avoid unnecessary spending." },
    ],
  },
  {
    key: "education_course", type: "life_event", title: "Online Course Opportunity 🎓",
    description: "A certification course that could boost your career costs $250. Worth it?",
    amount: 250, difficulty: "mid",
    options: [
      { label: "Invest in yourself (Checking)", accountType: "checking", amount: 250, isDeduction: true, explanation: "Education is one of the best investments. Skills compound like interest." },
      { label: "Use Savings", accountType: "savings", amount: 250, isDeduction: true, explanation: "Using savings for education is strategic spending, not wasteful spending." },
      { label: "Wait for a free option", accountType: "checking", amount: 0, isDeduction: false, explanation: "Being frugal is smart, but sometimes paying for quality education accelerates your career." },
    ],
  },
  {
    key: "investment_opportunity", type: "life_event", title: "Investment Opportunity 📊",
    description: "A friend offers you a chance to invest $300 in a small business they're starting.",
    amount: 300, difficulty: "advanced",
    options: [
      { label: "Invest from Checking", accountType: "checking", amount: 300, isDeduction: true, explanation: "Small business investments are risky but can have high returns. Only invest what you can afford to lose." },
      { label: "Use investment account", accountType: "investment", amount: 300, isDeduction: true, explanation: "Using your investment account keeps risk capital separate from daily spending." },
      { label: "Pass on it", accountType: "checking", amount: 0, isDeduction: false, explanation: "Not every opportunity is right for you. Saying no is a valid financial strategy." },
    ],
  },
  {
    key: "phone_replacement", type: "life_event", title: "Phone Replacement 📱",
    description: "Your phone died completely. A replacement costs $320.",
    amount: 320, difficulty: "mid",
    options: [
      { label: "Buy from Checking", accountType: "checking", amount: 320, isDeduction: true, explanation: "A phone is essential. Replacing it from checking is practical." },
      { label: "Use Emergency Fund", accountType: "emergency_fund", amount: 320, isDeduction: true, explanation: "A dead phone is an emergency. Your fund is designed for this." },
      { label: "Buy refurbished ($180)", accountType: "checking", amount: 180, isDeduction: true, explanation: "Buying refurbished saves money and still gets you a working phone. Smart move!" },
    ],
  },
];

/* ─── Economic Events ─── */

const ECONOMIC_EVENTS: EventTemplate[] = [
  {
    key: "inflation_spike", type: "economic_event", title: "Inflation Spike 📈",
    description: "Prices are rising 6% this year. Your purchasing power is shrinking. How do you respond?",
    amount: 200, difficulty: "mid",
    options: [
      { label: "Cut discretionary spending", accountType: "checking", amount: 100, isDeduction: false, explanation: "Reducing spending during inflation preserves your real purchasing power." },
      { label: "Move cash to investments", accountType: "investment", amount: 200, isDeduction: false, explanation: "Investments can outpace inflation over time, protecting your wealth from erosion." },
      { label: "Do nothing", accountType: "checking", amount: 0, isDeduction: false, explanation: "Ignoring inflation means your savings lose value. Every dollar buys less next year." },
    ],
  },
  {
    key: "recession_warning", type: "economic_event", title: "Recession Warning ⚠️",
    description: "Economic indicators suggest a recession is coming. Unemployment may rise. What's your move?",
    amount: 300, difficulty: "advanced",
    options: [
      { label: "Build emergency reserves", accountType: "emergency_fund", amount: 300, isDeduction: false, explanation: "Increasing your safety net before a downturn is one of the smartest defensive moves." },
      { label: "Reduce investment risk", accountType: "savings", amount: 200, isDeduction: false, explanation: "Moving to safer assets before a downturn reduces volatility but may miss recovery gains." },
      { label: "Stay the course", accountType: "checking", amount: 0, isDeduction: false, explanation: "Long-term investors who stay invested through downturns historically outperform market timers." },
    ],
  },
  {
    key: "interest_rate_hike", type: "economic_event", title: "Interest Rate Increase 🏦",
    description: "The central bank raised interest rates by 0.75%. Borrowing costs are up, but savings rates improve.",
    amount: 50, difficulty: "mid",
    options: [
      { label: "Move cash to high-yield savings", accountType: "savings", amount: 50, isDeduction: false, explanation: "Higher rates mean your savings earn more. Take advantage of improved yields." },
      { label: "Pay down variable-rate debt", accountType: "checking", amount: 100, isDeduction: true, explanation: "Rising rates make variable debt more expensive. Paying it down saves money." },
      { label: "Lock in fixed-rate investments", accountType: "investment", amount: 100, isDeduction: false, explanation: "Fixed-rate bonds become more attractive when rates rise. Locking in can be smart." },
    ],
  },
  {
    key: "labor_shortage", type: "economic_event", title: "Labor Shortage 👷",
    description: "Companies are struggling to find workers. Wages are rising in key industries.",
    amount: 150, difficulty: "mid",
    options: [
      { label: "Negotiate a raise", accountType: "checking", amount: 150, isDeduction: false, explanation: "Labor shortages give workers bargaining power. This is the time to negotiate." },
      { label: "Invest in new skills", accountType: "checking", amount: 100, isDeduction: true, explanation: "Upskilling during a labor shortage positions you for even higher-paying roles." },
      { label: "Stay put, save bonus", accountType: "savings", amount: 100, isDeduction: false, explanation: "Stability has value too, especially if your employer offers retention bonuses." },
    ],
  },
  {
    key: "commodity_shock", type: "economic_event", title: "Commodity Price Shock ⛽",
    description: "Oil prices surged 40%. Gas, food, and shipping costs are all up.",
    amount: 150, difficulty: "advanced",
    options: [
      { label: "Cut transportation costs", accountType: "checking", amount: 75, isDeduction: false, explanation: "Reducing driving, carpooling, or using transit saves real money during price spikes." },
      { label: "Stock up on essentials", accountType: "checking", amount: 150, isDeduction: true, explanation: "Buying essentials before further increases can save money, but ties up cash." },
      { label: "Absorb the cost", accountType: "checking", amount: 0, isDeduction: false, explanation: "Commodity shocks are usually temporary. Adjusting spending is wiser than panic buying." },
    ],
  },
  {
    key: "policy_stimulus", type: "economic_event", title: "Government Stimulus 💵",
    description: "The government announced a $500 stimulus payment to boost the economy. How do you use it?",
    amount: 500, difficulty: "early",
    options: [
      { label: "Save it all", accountType: "savings", amount: 500, isDeduction: false, explanation: "Saving stimulus money builds reserves. The government wants you to spend, but saving is smart." },
      { label: "Invest for growth", accountType: "investment", amount: 500, isDeduction: false, explanation: "Investing a windfall can grow it significantly over time through compound returns." },
      { label: "Pay down debt", accountType: "checking", amount: 500, isDeduction: true, explanation: "Using stimulus to eliminate debt reduces your future interest burden permanently." },
    ],
  },
  {
    key: "housing_shortage", type: "economic_event", title: "Housing Supply Shortage 🏠",
    description: "Housing inventory is at historic lows. Rents are rising 15% in your area.",
    amount: 200, difficulty: "advanced",
    options: [
      { label: "Lock in current rent", accountType: "checking", amount: 0, isDeduction: false, explanation: "Signing a longer lease at current rates protects you from further increases." },
      { label: "Move to cheaper area", accountType: "checking", amount: 200, isDeduction: true, explanation: "Geographic arbitrage can dramatically reduce housing costs, but consider commute tradeoffs." },
      { label: "Increase housing budget", accountType: "checking", amount: 200, isDeduction: true, explanation: "Paying more for housing reduces money available for savings and investments." },
    ],
  },
  {
    key: "trade_disruption", type: "economic_event", title: "Trade Disruption 🚢",
    description: "New tariffs increased import prices by 25%. Consumer goods are getting more expensive.",
    amount: 100, difficulty: "advanced",
    options: [
      { label: "Buy domestic alternatives", accountType: "checking", amount: 50, isDeduction: true, explanation: "Domestic goods may cost more but aren't affected by tariffs. Trade policy changes consumer behavior." },
      { label: "Delay major purchases", accountType: "savings", amount: 100, isDeduction: false, explanation: "Waiting for prices to stabilize is often wise during trade disruptions." },
      { label: "Stock up before prices rise more", accountType: "checking", amount: 100, isDeduction: true, explanation: "If tariffs are expected to increase, buying now at lower prices makes sense." },
    ],
  },
  {
    key: "rate_cut", type: "economic_event", title: "Interest Rate Cut 📉",
    description: "The central bank cut rates to stimulate growth. Borrowing is cheaper, but savings earn less.",
    amount: 100, difficulty: "mid",
    options: [
      { label: "Refinance debt", accountType: "checking", amount: 100, isDeduction: false, explanation: "Lower rates make refinancing profitable. Reducing interest costs frees up cash flow." },
      { label: "Borrow to invest", accountType: "investment", amount: 200, isDeduction: false, explanation: "Cheap debt for productive assets can amplify returns, but adds risk." },
      { label: "Move savings to investments", accountType: "investment", amount: 150, isDeduction: false, explanation: "When savings rates drop, investments become relatively more attractive." },
    ],
  },
  {
    key: "credit_tightening", type: "economic_event", title: "Credit Tightening 🔒",
    description: "Banks are tightening lending standards. Loans and credit cards are harder to get.",
    amount: 0, difficulty: "advanced",
    options: [
      { label: "Strengthen credit score", accountType: "checking", amount: 0, isDeduction: false, explanation: "In tight credit markets, a strong score is your best asset. Pay down balances." },
      { label: "Build cash reserves", accountType: "emergency_fund", amount: 200, isDeduction: false, explanation: "When credit tightens, cash is king. Reserves replace borrowing capacity." },
      { label: "Delay major purchases", accountType: "savings", amount: 100, isDeduction: false, explanation: "Waiting for credit conditions to ease can save you significantly on borrowing costs." },
    ],
  },
  {
    key: "banking_confidence_shock", type: "economic_event", title: "Banking Confidence Shock 🏦",
    description: "A regional bank failed. Depositors everywhere are nervous. Your accounts are FDIC-insured.",
    amount: 0, difficulty: "advanced",
    options: [
      { label: "Stay calm — FDIC covers you", accountType: "checking", amount: 0, isDeduction: false, explanation: "FDIC insurance protects deposits up to $250K. Understanding this prevents panic." },
      { label: "Diversify across institutions", accountType: "savings", amount: 100, isDeduction: false, explanation: "Spreading deposits across banks maximizes FDIC coverage for large balances." },
      { label: "Move to cash", accountType: "checking", amount: 200, isDeduction: false, explanation: "Hoarding cash feels safe but earns nothing. The banking system has strong safeguards." },
    ],
  },
  {
    key: "consumer_demand_boom", type: "economic_event", title: "Consumer Demand Boom 🛍️",
    description: "Consumer confidence is surging. Spending is up 20% and businesses are hiring rapidly.",
    amount: 200, difficulty: "mid",
    options: [
      { label: "Invest in growth stocks", accountType: "investment", amount: 200, isDeduction: false, explanation: "Consumer booms lift company earnings. Investing during expansion captures upside." },
      { label: "Negotiate a raise", accountType: "checking", amount: 200, isDeduction: false, explanation: "Booming demand means employers compete for talent. Leverage this for higher pay." },
      { label: "Save — booms don't last", accountType: "savings", amount: 200, isDeduction: false, explanation: "Saving during good times builds the cushion you'll need when conditions change." },
    ],
  },
  {
    key: "asset_bubble_warning", type: "economic_event", title: "Asset Bubble Warning 🫧",
    description: "Asset prices are disconnected from fundamentals. Speculation is rampant.",
    amount: 0, difficulty: "advanced",
    options: [
      { label: "Reduce speculative positions", accountType: "savings", amount: 200, isDeduction: false, explanation: "Trimming risk before a correction is the mark of disciplined investing." },
      { label: "Stay invested — timing is hard", accountType: "checking", amount: 0, isDeduction: false, explanation: "Trying to time bubbles often costs more than riding through them. Stay diversified." },
      { label: "Build cash position", accountType: "emergency_fund", amount: 300, isDeduction: false, explanation: "Cash positions let you buy quality assets cheaply after bubbles pop." },
    ],
  },
  {
    key: "supply_chain_bottleneck", type: "economic_event", title: "Supply Chain Bottleneck 🚛",
    description: "Global shipping delays are causing shortages. Wait times are tripling for consumer goods.",
    amount: 100, difficulty: "mid",
    options: [
      { label: "Buy essentials early", accountType: "checking", amount: 100, isDeduction: true, explanation: "Stocking up on necessities before shortages worsen is pragmatic planning." },
      { label: "Delay non-essential purchases", accountType: "savings", amount: 100, isDeduction: false, explanation: "Patience during supply disruptions saves money as prices normalize." },
      { label: "Invest in logistics companies", accountType: "investment", amount: 100, isDeduction: false, explanation: "Companies solving bottlenecks often profit. Investing in solutions is strategic." },
    ],
  },
  {
    key: "tax_policy_shift", type: "economic_event", title: "Tax Policy Change 📋",
    description: "New tax legislation changes deduction rules and bracket thresholds.",
    amount: 100, difficulty: "mid",
    options: [
      { label: "Maximize new deductions", accountType: "checking", amount: 100, isDeduction: false, explanation: "Understanding tax changes lets you capture savings others miss." },
      { label: "Increase retirement contributions", accountType: "roth_ira", amount: 200, isDeduction: false, explanation: "Tax-advantaged accounts become more valuable when tax rules shift." },
      { label: "Consult a tax professional", accountType: "checking", amount: 50, isDeduction: true, explanation: "Professional advice during tax changes can save multiples of the consultation cost." },
    ],
  },
  {
    key: "wage_slowdown", type: "economic_event", title: "Wage Growth Slowdown 💼",
    description: "Wage growth has stalled while costs keep rising. Your real income is declining.",
    amount: 0, difficulty: "mid",
    options: [
      { label: "Upskill for higher-paying role", accountType: "checking", amount: 150, isDeduction: true, explanation: "Investing in skills during wage stagnation positions you for the next cycle." },
      { label: "Cut expenses aggressively", accountType: "savings", amount: 100, isDeduction: false, explanation: "When income stalls, controlling expenses preserves your savings rate." },
      { label: "Start a side income", accountType: "checking", amount: 100, isDeduction: false, explanation: "Diversifying income sources reduces dependence on a single stagnant wage." },
    ],
  },
  {
    key: "price_sensitivity", type: "entrepreneurship_event", title: "Price Sensitivity Spike 💲",
    description: "Customers are pushing back on your pricing. Conversion rates dropped 25%.",
    amount: 0, difficulty: "mid",
    options: [
      { label: "Lower prices", accountType: "checking", amount: 100, isDeduction: true, explanation: "Price cuts boost volume but erode margins. Make sure unit economics still work." },
      { label: "Add more value at same price", accountType: "checking", amount: 50, isDeduction: true, explanation: "Increasing perceived value is more sustainable than cutting prices." },
      { label: "Segment pricing", accountType: "checking", amount: 0, isDeduction: false, explanation: "Offering tiers lets price-sensitive buyers in without devaluing your premium offer." },
    ],
  },
  {
    key: "quality_issue", type: "entrepreneurship_event", title: "Product Quality Issue 🔍",
    description: "Multiple customers reported the same defect. Returns are spiking.",
    amount: 200, difficulty: "mid",
    options: [
      { label: "Full recall and fix", accountType: "checking", amount: 200, isDeduction: true, explanation: "Proactive recalls are expensive but protect long-term brand trust." },
      { label: "Fix quietly going forward", accountType: "checking", amount: 50, isDeduction: true, explanation: "Fixing silently saves money short-term but risks reputation if customers notice." },
      { label: "Issue refunds and communicate", accountType: "checking", amount: 150, isDeduction: true, explanation: "Transparency with refunds turns unhappy customers into loyal advocates." },
    ],
  },
  {
    key: "cofounder_conflict", type: "entrepreneurship_event", title: "Cofounder Disagreement ⚔️",
    description: "You and your cofounder disagree on a major strategic direction. Tension is rising.",
    amount: 0, difficulty: "advanced",
    options: [
      { label: "Structured mediation", accountType: "checking", amount: 100, isDeduction: true, explanation: "Professional mediation resolves conflicts before they become existential threats." },
      { label: "Define clear roles", accountType: "checking", amount: 0, isDeduction: false, explanation: "Role clarity prevents future conflicts. Most cofounder issues stem from ambiguous authority." },
      { label: "Buyout discussion", accountType: "checking", amount: 0, isDeduction: false, explanation: "Sometimes parting ways is the healthiest option. Better early than after irreversible damage." },
    ],
  },
  {
    key: "platform_dependency", type: "entrepreneurship_event", title: "Platform Dependency Risk 📱",
    description: "The platform you depend on for 70% of revenue changed its algorithm. Traffic dropped 40%.",
    amount: 200, difficulty: "advanced",
    options: [
      { label: "Diversify channels immediately", accountType: "checking", amount: 200, isDeduction: true, explanation: "Platform risk is real. Diversifying distribution protects against single-point failures." },
      { label: "Build owned audience (email/SMS)", accountType: "checking", amount: 100, isDeduction: true, explanation: "Owned channels can't be taken away. Email lists are your most durable asset." },
      { label: "Adapt to the new algorithm", accountType: "checking", amount: 0, isDeduction: false, explanation: "Adapting works short-term, but you'll face the same risk at the next change." },
    ],
  },
  {
    key: "investor_offer", type: "entrepreneurship_event", title: "Investor Approaches You 🤝",
    description: "An investor wants to put $50K into your business for 20% equity. Do you take it?",
    amount: 0, difficulty: "advanced",
    options: [
      { label: "Accept the investment", accountType: "checking", amount: 500, isDeduction: false, explanation: "Capital accelerates growth, but you're giving up ownership and control. Choose investors carefully." },
      { label: "Negotiate better terms", accountType: "checking", amount: 0, isDeduction: false, explanation: "Negotiating shows confidence in your business. Better terms preserve more upside for you." },
      { label: "Decline — stay bootstrapped", accountType: "checking", amount: 0, isDeduction: false, explanation: "Bootstrapping keeps full control. Not every business needs outside capital to succeed." },
    ],
  },
  {
    key: "client_nonpayment", type: "entrepreneurship_event", title: "Client Nonpayment 🚫",
    description: "A major client owes you $2,000 and has gone silent for 30 days.",
    amount: 200, difficulty: "mid",
    options: [
      { label: "Send formal demand", accountType: "checking", amount: 0, isDeduction: false, explanation: "Formal collection processes protect your cash flow. Don't let invoices slide." },
      { label: "Offer payment plan", accountType: "checking", amount: 100, isDeduction: false, explanation: "Payment plans recover some revenue and preserve the relationship." },
      { label: "Write it off and move on", accountType: "checking", amount: 200, isDeduction: true, explanation: "Sometimes chasing bad debt costs more than the debt itself. Learn and tighten terms." },
    ],
  },
  {
    key: "reputation_issue", type: "entrepreneurship_event", title: "Negative Review Viral 📢",
    description: "A 1-star review went viral. Your online rating dropped and inquiries are falling.",
    amount: 100, difficulty: "mid",
    options: [
      { label: "Respond publicly and fix it", accountType: "checking", amount: 100, isDeduction: true, explanation: "Transparent responses to criticism often win more trust than perfect scores." },
      { label: "Invest in reputation management", accountType: "checking", amount: 150, isDeduction: true, explanation: "Proactive reputation management prevents small issues from becoming big ones." },
      { label: "Focus on product improvement", accountType: "checking", amount: 0, isDeduction: false, explanation: "The best reputation strategy is making the product so good reviews reverse naturally." },
    ],
  },
  {
    key: "rising_cac", type: "entrepreneurship_event", title: "Rising Acquisition Costs 📊",
    description: "Your customer acquisition cost doubled in 3 months. Paid channels are getting expensive.",
    amount: 150, difficulty: "advanced",
    options: [
      { label: "Double down on organic growth", accountType: "checking", amount: 100, isDeduction: true, explanation: "Content, referrals, and community have lower CAC and compound over time." },
      { label: "Improve conversion rate", accountType: "checking", amount: 50, isDeduction: true, explanation: "Optimizing conversion gets more customers from the same traffic at no extra cost." },
      { label: "Raise prices to maintain margins", accountType: "checking", amount: 0, isDeduction: false, explanation: "If your product delivers strong value, the market may absorb a price increase." },
    ],
  },
  {
    key: "partnership_opportunity", type: "entrepreneurship_event", title: "Partnership Opportunity 🤝",
    description: "A complementary business proposes a joint venture that could double your distribution.",
    amount: 0, difficulty: "mid",
    options: [
      { label: "Accept and co-market", accountType: "checking", amount: 100, isDeduction: true, explanation: "Partnerships expand reach at shared cost. Ensure aligned values and clear terms." },
      { label: "Test with a small pilot first", accountType: "checking", amount: 50, isDeduction: true, explanation: "Piloting partnerships before full commitment reduces downside risk." },
      { label: "Decline — stay focused", accountType: "checking", amount: 0, isDeduction: false, explanation: "Not every partnership is right. Focus is a competitive advantage." },
    ],
  },
  {
    key: "expansion_temptation", type: "entrepreneurship_event", title: "Expansion Temptation 🌍",
    description: "A new market looks promising but requires $500 upfront and will stretch your team thin.",
    amount: 500, difficulty: "advanced",
    options: [
      { label: "Expand aggressively", accountType: "checking", amount: 500, isDeduction: true, explanation: "Aggressive expansion captures opportunity but risks overextending resources." },
      { label: "Expand slowly with a test", accountType: "checking", amount: 150, isDeduction: true, explanation: "Testing before scaling validates demand without betting the company." },
      { label: "Stay focused on core market", accountType: "checking", amount: 0, isDeduction: false, explanation: "Depth often beats breadth. Dominating one market is more durable than spreading thin." },
    ],
  },
  {
    key: "legal_compliance", type: "entrepreneurship_event", title: "Legal & Compliance Issue ⚖️",
    description: "A new regulation affects your product. You need legal review and potential changes.",
    amount: 300, difficulty: "advanced",
    options: [
      { label: "Hire a lawyer immediately", accountType: "checking", amount: 300, isDeduction: true, explanation: "Legal compliance isn't optional. Proactive legal investment prevents much larger penalties." },
      { label: "Research and self-comply", accountType: "checking", amount: 50, isDeduction: true, explanation: "Self-education works for simple compliance but risks missing nuances in complex regulations." },
      { label: "Pivot product to avoid regulation", accountType: "checking", amount: 100, isDeduction: true, explanation: "Adapting your product can be smart, but don't let regulations drive strategy entirely." },
    ],
  },
  {
    key: "customer_assumption_wrong", type: "entrepreneurship_event", title: "Wrong Assumptions Revealed 💡",
    description: "Customer feedback shows your core assumption about what they value was wrong.",
    amount: 0, difficulty: "mid",
    options: [
      { label: "Pivot based on feedback", accountType: "checking", amount: 150, isDeduction: true, explanation: "Pivoting to real demand is strength, not failure. The best founders listen." },
      { label: "Run deeper customer interviews", accountType: "checking", amount: 50, isDeduction: true, explanation: "Before pivoting, validate the new direction. One round of feedback isn't enough." },
      { label: "Ignore and push forward", accountType: "checking", amount: 0, isDeduction: false, explanation: "Ignoring customer signals is how businesses die. Stubbornness isn't perseverance." },
    ],
  },
  {
    key: "ad_underperformance", type: "entrepreneurship_event", title: "Ad Campaign Flopped 📉",
    description: "Your paid advertising campaign spent $300 and generated only 2 leads.",
    amount: 300, difficulty: "mid",
    options: [
      { label: "Analyze and optimize", accountType: "checking", amount: 50, isDeduction: true, explanation: "Failed campaigns teach you what doesn't work. Analyze before spending more." },
      { label: "Shift to organic channels", accountType: "checking", amount: 0, isDeduction: false, explanation: "Organic growth is slower but more sustainable. Not every business needs paid ads." },
      { label: "Try a different platform", accountType: "checking", amount: 200, isDeduction: true, explanation: "Different platforms reach different audiences. Test before committing big budgets." },
    ],
  },
];

/* ─── Entrepreneurship Events ─── */

const ENTREPRENEURSHIP_EVENTS: EventTemplate[] = [
  {
    key: "customer_churn", type: "entrepreneurship_event", title: "Customer Churn Spike 📉",
    description: "Your top 3 clients just cancelled. Revenue dropped 30% overnight. How do you respond?",
    amount: 300, difficulty: "mid",
    options: [
      { label: "Cut costs immediately", accountType: "checking", amount: 150, isDeduction: true, explanation: "Reducing burn rate buys time. Fast cost-cutting preserves runway when revenue drops." },
      { label: "Double down on retention", accountType: "checking", amount: 100, isDeduction: true, explanation: "Investing in existing customers is cheaper than acquiring new ones. Retention compounds." },
      { label: "Pivot to new segment", accountType: "checking", amount: 200, isDeduction: true, explanation: "Pivoting can work but costs time and money. Make sure there's validated demand first." },
    ],
  },
  {
    key: "word_of_mouth", type: "entrepreneurship_event", title: "Word-of-Mouth Growth 🗣️",
    description: "Your customers are referring friends. Demand is surging 50% above your capacity!",
    amount: 400, difficulty: "mid",
    options: [
      { label: "Hire to meet demand", accountType: "checking", amount: 400, isDeduction: true, explanation: "Hiring fast captures demand but increases fixed costs. Make sure growth is sustainable." },
      { label: "Raise prices", accountType: "checking", amount: 200, isDeduction: false, explanation: "When demand exceeds supply, raising prices filters for best customers and improves margins." },
      { label: "Waitlist new customers", accountType: "checking", amount: 0, isDeduction: false, explanation: "A waitlist creates scarcity and lets you scale carefully without sacrificing quality." },
    ],
  },
  {
    key: "supplier_delay", type: "entrepreneurship_event", title: "Supplier Delay 📦",
    description: "Your primary supplier is 3 weeks late. Customers are waiting and trust is at risk.",
    amount: 200, difficulty: "mid",
    options: [
      { label: "Find backup supplier (premium)", accountType: "checking", amount: 200, isDeduction: true, explanation: "Backup suppliers cost more but protect your reputation. Supply chain diversification matters." },
      { label: "Communicate transparently", accountType: "checking", amount: 0, isDeduction: false, explanation: "Honest communication preserves trust. Most customers respect transparency over silence." },
      { label: "Offer discounts for patience", accountType: "checking", amount: 100, isDeduction: true, explanation: "Discounts retain customers but reduce margins. Use sparingly during supply disruptions." },
    ],
  },
  {
    key: "competitor_enters", type: "entrepreneurship_event", title: "New Competitor 🏁",
    description: "A well-funded competitor just launched a similar product at a lower price.",
    amount: 0, difficulty: "advanced",
    options: [
      { label: "Differentiate on quality", accountType: "checking", amount: 150, isDeduction: true, explanation: "Competing on value, not price, builds a more durable business. Quality creates loyalty." },
      { label: "Match their price", accountType: "checking", amount: 200, isDeduction: true, explanation: "Price matching erodes margins. You can't outspend a well-funded competitor on price." },
      { label: "Focus on existing customers", accountType: "checking", amount: 0, isDeduction: false, explanation: "Deepening relationships with current customers creates switching costs competitors can't easily overcome." },
    ],
  },
  {
    key: "cash_crunch", type: "entrepreneurship_event", title: "Cash Crunch 💸",
    description: "Two major clients paid late. You have 2 weeks of runway left. What do you do?",
    amount: 500, difficulty: "advanced",
    options: [
      { label: "Cut all non-essential costs", accountType: "checking", amount: 200, isDeduction: false, explanation: "Preserving cash during a crunch is survival. Cut deep and fast, restore later." },
      { label: "Offer early-pay discounts", accountType: "checking", amount: 100, isDeduction: true, explanation: "2% discounts for early payment can unlock cash flow when you need it most." },
      { label: "Seek emergency financing", accountType: "checking", amount: 500, isDeduction: false, explanation: "Emergency credit costs interest but keeps the business alive. Use as a bridge, not a crutch." },
    ],
  },
  {
    key: "hiring_challenge", type: "entrepreneurship_event", title: "Hiring Challenge 👥",
    description: "Your best employee just quit to start their own business. A critical role is now empty.",
    amount: 250, difficulty: "mid",
    options: [
      { label: "Hire quickly at premium", accountType: "checking", amount: 250, isDeduction: true, explanation: "Fast hires cost more but prevent operational gaps. Speed matters when a key role is empty." },
      { label: "Promote internally", accountType: "checking", amount: 50, isDeduction: true, explanation: "Internal promotion saves money and shows the team there's a growth path. Loyalty builds culture." },
      { label: "Redistribute the work", accountType: "checking", amount: 0, isDeduction: false, explanation: "Temporary redistribution buys time but risks burnout. Don't make it permanent." },
    ],
  },
  {
    key: "demand_surge", type: "entrepreneurship_event", title: "Viral Demand Surge 🚀",
    description: "A social media post went viral. Orders are 5x your normal volume!",
    amount: 300, difficulty: "advanced",
    options: [
      { label: "Scale operations fast", accountType: "checking", amount: 300, isDeduction: true, explanation: "Capturing viral demand requires fast investment. But ensure the surge converts to repeat customers." },
      { label: "Fulfill what you can, waitlist rest", accountType: "checking", amount: 0, isDeduction: false, explanation: "Controlled fulfillment protects quality. A waitlist builds anticipation and filters serious buyers." },
      { label: "Raise prices during surge", accountType: "checking", amount: 200, isDeduction: false, explanation: "Dynamic pricing captures value from peak demand. Common in many industries." },
    ],
  },
  {
    key: "burnout_pressure", type: "entrepreneurship_event", title: "Founder Burnout ⚡",
    description: "You've been working 80-hour weeks for months. Quality is slipping and you're exhausted.",
    amount: 0, difficulty: "advanced",
    options: [
      { label: "Delegate and rest", accountType: "checking", amount: 150, isDeduction: true, explanation: "Hiring help costs money but prevents total collapse. Your judgment deteriorates when exhausted." },
      { label: "Push through", accountType: "checking", amount: 0, isDeduction: false, explanation: "Grinding through burnout is unsustainable. Research shows diminishing returns past 50 hours/week." },
      { label: "Reduce scope temporarily", accountType: "checking", amount: 0, isDeduction: false, explanation: "Focusing on fewer things at higher quality beats doing everything poorly. Strategic retreat wins wars." },
    ],
  },
];

/* ─── Difficulty progression ─── */

function getProgressionStage(lessonsCompleted: number): "early" | "mid" | "advanced" {
  if (lessonsCompleted < 8) return "early";
  if (lessonsCompleted < 20) return "mid";
  return "advanced";
}

/* ─── Hook ─── */

export interface IncomeStream {
  id: string;
  stream_type: string;
  stream_name: string;
  multiplier_bonus: number;
  passive_payout: number;
  source_module: string | null;
  active: boolean;
}

export function useEventEngine() {
  const { user } = useAuth();
  const { activated: careerActivated, state: careerState } = useCareerActivation();
  const [activeEvent, setActiveEvent] = useState<ActiveEvent | null>(null);
  const [incomeStreams, setIncomeStreams] = useState<IncomeStream[]>([]);
  const [multiplier, setMultiplier] = useState(1.0);
  const [loading, setLoading] = useState(true);
  const lessonsSinceEvent = useRef(0);

  // Fetch income streams and multiplier
  const fetchStreams = useCallback(async () => {
    if (!user) { setIncomeStreams([]); setMultiplier(1.0); setLoading(false); return; }
    const { data } = await supabase
      .from("income_streams")
      .select("*")
      .eq("user_id", user.id)
      .eq("active", true);
    setIncomeStreams((data) || []);

    const { data: mult } = await supabase.rpc("get_earnings_multiplier");
    setMultiplier(typeof mult === "number" ? mult : 1.0);
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchStreams(); }, [fetchStreams]);

  // Count lessons since last event — persisted across page navigations
  const initLessonCounter = useCallback(async () => {
    if (!user) return;
    // Get the last event timestamp
    const { data: lastEvent } = await supabase
      .from("user_event_log")
      .select("created_at")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(1);

    if (lastEvent && lastEvent.length > 0) {
      const lastEventDate = (lastEvent[0]).created_at;
      // Count completed lessons after the last event using account_transactions completion bonuses
      const { count: lessonsSince } = await supabase
        .from("account_transactions")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("transaction_type", "completion_bonus")
        .gt("created_at", lastEventDate);
      lessonsSinceEvent.current = lessonsSince || 0;
    } else {
      // No events yet — count all completed lessons
      const { count } = await supabase
        .from("user_progress")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("completed", true);
      lessonsSinceEvent.current = count || 0;
    }
    console.log("[EventEngine] initialized lessonsSinceEvent:", lessonsSinceEvent.current);
  }, [user]);

  useEffect(() => { initLessonCounter(); }, [initLessonCounter]);

  // Should trigger an event after this lesson?
  const shouldTriggerEvent = useCallback((lessonsCompleted: number): "money_event" | "life_event" | "economic_event" | "entrepreneurship_event" | null => {
    lessonsSinceEvent.current += 1;
    const since = lessonsSinceEvent.current;

    if (since >= 2 && since % 2 === 0) {
      const lifeChance = since >= 5 ? 1.0 : 0.4;
      if (Math.random() < lifeChance) {
        const roll = Math.random();
        lessonsSinceEvent.current = 0;
        // 30% money, 30% life, 25% economic, 15% entrepreneurship.
        // Entrepreneurship is gated behind Career Path activation — when not
        // activated, redistribute that weight to plain life events so the
        // general event system keeps firing.
        if (roll < 0.30) return "money_event";
        if (roll < 0.60) return "life_event";
        if (roll < 0.85) return "economic_event";
        return careerActivated ? "entrepreneurship_event" : "life_event";
      }
    }

    if (since >= 3) {
      const moneyChance = since >= 6 ? 1.0 : 0.35;
      if (Math.random() < moneyChance) {
        lessonsSinceEvent.current = 0;
        return "money_event";
      }
    }

    return null;
  }, [careerActivated]);

  // Pick a random event template
  const pickEvent = useCallback((type: "money_event" | "life_event" | "economic_event" | "entrepreneurship_event", lessonsCompleted: number): EventTemplate => {
    const stage = getProgressionStage(lessonsCompleted);
    let pool: EventTemplate[] =
      type === "money_event" ? MONEY_EVENTS :
      type === "economic_event" ? ECONOMIC_EVENTS :
      type === "entrepreneurship_event" ? ENTREPRENEURSHIP_EVENTS :
      LIFE_EVENTS;

    // When Career Path is activated, merge job + side-hustle bank into the
    // appropriate pools so career events can surface alongside everything else.
    if (careerActivated) {
      const careerBank = eligibleCareerEvents(
        careerState?.career_key ?? null,
        careerState?.side_hustle_key ?? null,
      );
      if (type === "life_event") {
        pool = [...pool, ...careerBank.filter(e => e.category === "job_event")];
      } else if (type === "entrepreneurship_event") {
        pool = [...pool, ...careerBank.filter(e => e.category === "side_hustle_event")];
      }
    }

    const staged = pool.filter(e => e.difficulty === stage);
    const candidates = staged.length >= 2 ? staged : pool;
    return candidates[Math.floor(Math.random() * candidates.length)];
  }, [careerActivated, careerState]);

  // Trigger event check (call after lesson completion)
  const checkAndTriggerEvent = useCallback((lessonId: string, lessonsCompleted: number) => {
    // General life/money/economic events always fire.
    // Job/career/side-hustle events are mixed in via pickEvent only when
    // Career Path is activated.
    const eventType = shouldTriggerEvent(lessonsCompleted);
    if (!eventType) return false;

    const template = pickEvent(eventType, lessonsCompleted);
    setActiveEvent({ template, lessonId });
    return true;
  }, [shouldTriggerEvent, pickEvent]);

  // Handle user's choice
  const resolveEvent = useCallback(async (choiceIndex: number) => {
    if (!activeEvent || !user) return;
    const { template, lessonId } = activeEvent;
    const option = template.options[choiceIndex];

    // Apply account impact
    try {
      if (option.amount > 0) {
        const eventLabel = template.type === "money_event" ? "Money Event" : template.type === "economic_event" ? "Economic Event" : template.type === "entrepreneurship_event" ? "Entrepreneurship Event" : "Life Event";
        await supabase.rpc("apply_event_impact", {
          p_account_type: option.accountType,
          p_amount: option.amount,
          p_description: `${eventLabel}: ${template.title} — ${option.label}`,
          p_is_deduction: option.isDeduction,
        });
      }

      // Handle credit option
      if (option.label.includes("Credit")) {
        await supabase.rpc("auto_credit_charge", {
          p_amount: template.amount,
          p_description: `Credit Purchase: ${template.title}`,
        });
      }
    } catch (err) {
      console.warn("Event impact error:", err);
    }

    // Log the event
    await supabase.from("user_event_log").insert({
      user_id: user.id,
      event_type: template.type,
      event_key: template.key,
      choice_index: choiceIndex,
      account_impact: {
        accountType: option.accountType,
        amount: option.amount,
        isDeduction: option.isDeduction,
      },
      lesson_id: lessonId,
      lessons_since_last_event: lessonsSinceEvent.current,
    });

    return option;
  }, [activeEvent, user]);

  const dismissEvent = useCallback(() => {
    setActiveEvent(null);
  }, []);

  return {
    activeEvent,
    incomeStreams,
    multiplier,
    loading,
    checkAndTriggerEvent,
    resolveEvent,
    dismissEvent,
    refetchStreams: fetchStreams,
  };
}
